import { useState } from 'react';
import { Card, Input, Button, Space, Typography, Alert, Tag, Select } from 'antd';
import { ExperimentOutlined, ReloadOutlined } from '@ant-design/icons';
import { metadataFormApi, type FormLayout } from '@api/importApi';
import { metadataApi } from '@api/metadataApi';
import BasFormRenderer from './BasFormRenderer';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ZERO_UUID = '00000000-0000-0000-0000-000000000000';

function collectUuids(obj: unknown): string[] {
  const out = new Set<string>();
  const walk = (v: unknown) => {
    if (typeof v === 'string' && UUID_RE.test(v) && v !== ZERO_UUID) {
      out.add(v);
    } else if (Array.isArray(v)) {
      v.forEach(walk);
    } else if (v && typeof v === 'object') {
      Object.values(v as Record<string, unknown>).forEach(walk);
    }
  };
  walk(obj);
  return Array.from(out);
}

const { Title, Text, Paragraph } = Typography;

const DEFAULT_FORM_PATH = 'd:/Project/ac-quantum/КонфВигрузка/Documents/ПоступлениеНаРасчетныйСчет/Forms/ФормаДокумента/Ext/Form.xml';
const DEFAULT_DOC_TYPE = 'postuplenie_na_raschetnyj_schet';

export default function BasFormDemoPage() {
  const [acqFormPath, setAcqFormPath] = useState<string>(DEFAULT_FORM_PATH);
  const [acqDocType, setAcqDocType] = useState<string>(DEFAULT_DOC_TYPE);
  const [acqLayout, setAcqLayout] = useState<FormLayout | null>(null);
  const [acqData, setAcqData] = useState<Record<string, unknown>>({});
  const [acqRefMap, setAcqRefMap] = useState<Record<string, string>>({});
  const [acqDocs, setAcqDocs] = useState<Array<{ id: string; docNumber?: string | null; docDate: string }>>([]);
  const [acqSelectedDoc, setAcqSelectedDoc] = useState<string | undefined>();
  const [acqLoading, setAcqLoading] = useState<boolean>(false);
  const [acqError, setAcqError] = useState<string | null>(null);

  async function runLoad() {
    setAcqLoading(true);
    setAcqError(null);
    try {
      const acqL = await metadataFormApi.getFormLayout(acqFormPath);
      setAcqLayout(acqL);

      const acqDocuments = await metadataApi.getDynamicDocuments(acqDocType, 1, 20);
      setAcqDocs(acqDocuments);
      if (acqDocuments.length > 0 && !acqSelectedDoc) {
        await loadDoc(acqDocuments[0].id);
      } else if (acqDocuments.length === 0) {
        setAcqData({});
      }
    } catch (e) {
      setAcqError((e as Error).message);
    } finally {
      setAcqLoading(false);
    }
  }

  async function loadDoc(acqId: string) {
    setAcqSelectedDoc(acqId);
    try {
      const acqFull = await metadataApi.getDynamicDocument(acqId);
      const acqParsed = acqFull.dataJson ? JSON.parse(acqFull.dataJson) : {};
      acqParsed.number = acqFull.docNumber ?? '';
      acqParsed.date = acqFull.docDate;
      acqParsed.posted = acqFull.isPosted;
      setAcqData(acqParsed);

      // Резолв BAS UUIDs → назви довідників
      const acqUuids = collectUuids(acqParsed);
      if (acqUuids.length > 0) {
        const acqResolved = await metadataFormApi.resolveRefs(acqUuids);
        const acqMap: Record<string, string> = {};
        for (const r of acqResolved) {
          if (r.displayName) acqMap[r.uuid] = r.displayName;
        }
        setAcqRefMap(acqMap);
      } else {
        setAcqRefMap({});
      }
    } catch (e) {
      setAcqError((e as Error).message);
    }
  }

  return (
    <div style={{ padding: 24, maxWidth: 1200 }}>
      <Title level={3}>
        <Space>
          <ExperimentOutlined />
          <span>BAS Форма (POC)</span>
          <Tag color="purple">experiment</Tag>
        </Space>
      </Title>
      <Paragraph type="secondary">
        Парсить <Text code>Form.xml</Text> з вивантаженої BAS-конфігурації і рендерить layout (групи, таби, поля).
        Дані беруться з імпортованого <Text code>DynamicDocument</Text> (Phase 2). Обмеження POC: тільки шапка,
        табличні частини (Table) не підтримуються.
      </Paragraph>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Input
            addonBefore="Шлях до Form.xml"
            value={acqFormPath}
            onChange={e => setAcqFormPath(e.target.value)}
            placeholder={DEFAULT_FORM_PATH}
          />
          <Input
            addonBefore="Код типу документа"
            value={acqDocType}
            onChange={e => setAcqDocType(e.target.value)}
            placeholder={DEFAULT_DOC_TYPE}
          />
          <Space>
            <Button type="primary" icon={<ReloadOutlined />} onClick={runLoad} loading={acqLoading}>
              Завантажити форму + документ
            </Button>
            {acqDocs.length > 0 && (
              <Select
                style={{ minWidth: 260 }}
                value={acqSelectedDoc}
                onChange={loadDoc}
                options={acqDocs.map(d => ({
                  value: d.id,
                  label: `№${d.docNumber ?? '-'} від ${new Date(d.docDate).toLocaleDateString('uk-UA')}`,
                }))}
              />
            )}
          </Space>
        </Space>
      </Card>

      {acqError && <Alert type="error" message={acqError} style={{ marginBottom: 12 }} closable onClose={() => setAcqError(null)} />}

      {acqLayout && (
        <Card>
          <BasFormRenderer nodes={acqLayout.nodes} data={acqData} refMap={acqRefMap} mode="view" />
        </Card>
      )}
    </div>
  );
}
